/**
 * Dynamic labels for the Find Time dropdown.
 *
 * The Figma shows each option with an appended date range computed
 * at open time, e.g.:
 *
 *   "Next weekday"     → "Next weekday (Mon, Apr 27)"
 *   "Next 2 weekdays"  → "Next 2 weekdays (27 Apr – 28 Apr)"
 *   "Next week"        → "Next week (27 Apr – 3 May)"
 *   "Next 2 weeks"     → "Next 2 weeks (27 Apr – 10 May)"
 *
 * Anchor is always **today**, regardless of the visible calendar
 * week, so the preview always matches what the walker will do on
 * click. See `walker.ts`.
 */
import moment from 'moment-timezone';
import type { FindTimeMode, FindTimeValue } from './modes';
import { weekdaysTargetFor } from './modes';
import { walkFindTime } from './walker';

export interface FormatLabelArgs {
  mode: FindTimeMode;
  nowMs: number;
  timezone: string;
  /**
   * Forwarded to the walker so the previewed range matches what
   * the click would actually pick. When set and the walker finds
   * nothing, label collapses to the base label (no parenthesised
   * range) since there's no real range to show.
   */
  availableDays?: ReadonlySet<string>;
}

export function formatFindTimeLabel({
  mode,
  nowMs,
  timezone,
  availableDays,
}: FormatLabelArgs): string {
  const { dateKeys } = walkFindTime({
    mode: mode.value,
    nowMs,
    timezone,
    availableDays,
  });

  if (dateKeys.length === 0) return mode.baseLabel;

  const first = moment.tz(dateKeys[0]!, 'YYYY-MM-DD', timezone);
  const last = moment.tz(
    dateKeys[dateKeys.length - 1]!,
    'YYYY-MM-DD',
    timezone
  );

  // "Next weekday" is a single-day selection.
  if (weekdaysTargetFor(mode.value) === 1) {
    return `${mode.baseLabel} (${first.format('ddd, MMM D')})`;
  }

  return `${mode.baseLabel} (${first.format('D MMM')} – ${last.format('D MMM')})`;
}

/** "N slots selected" counter copy. Handles 0 / 1 / many. */
export function formatSlotsSelected(count: number): string {
  if (count === 0) return 'No slots selected';
  if (count === 1) return '1 slot selected';
  return `${count} slots selected`;
}

/** Convert a `FindTimeValue` back to its active mode label without
 *  a date range — used by the trigger button when a mode is
 *  active. */
export function baseLabelFor(
  value: FindTimeValue,
  modes: FindTimeMode[]
): string {
  if (value === null) return 'Find time this week';
  if (value === 'this_week') return 'Find time this week';
  return modes.find((m) => m.value === value)?.baseLabel ?? 'Find time';
}
